import { pdfDefaultOptions } from 'ngx-extended-pdf-viewer';

/**
 * ngx-extended-pdf-viewer 21.4.6 resolves the viewer and the pdf.js worker from two
 * different flags:
 *
 *   - the viewer  -> PDFScriptLoaderService._needsES5, set by the runtime feature detection
 *   - the worker  -> pdfDefaultOptions.needsES5, only written when [forceUsingLegacyES5] is set
 *
 * pdfDefaultOptions.needsES5 is initialised from a `ReadableStream` / `Promise.allSettled`
 * heuristic (Chrome 76 features), but the modern pdf.js 4.6 worker bundle actually calls
 * `Promise.withResolvers` (Chrome 119). On a browser in between - e.g. the Chromium 94 of
 * Electron 15 - the detection correctly picks the ES5 viewer while the worker stays on the
 * modern bundle, and the worker dies with:
 *
 *   Uncaught TypeError: Promise.withResolvers is not a function
 *
 * The library's own polyfill only patches the main thread, so it cannot help the worker,
 * which is a separate realm. Forcing pdfDefaultOptions.needsES5 keeps both artifacts on the
 * ES5 build.
 *
 * The check must be made against a fresh realm: zone.js 0.13.3 overwrites
 * `Promise.withResolvers` with `undefined` on the main window, so testing the global Promise
 * would report a false negative on browsers that do support it.
 */
export function applyPdfWorkerEs5WorkaroundIfNeeded(): void {
  if (supportsPromiseWithResolvers() === false) {
    pdfDefaultOptions.needsES5 = true;
  }
}

/**
 * @returns true / false when the check could be made, undefined when it could not - in which
 * case the library's own defaults are left untouched.
 */
function supportsPromiseWithResolvers(): boolean | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const host = document.body || document.documentElement;
  if (!host) {
    return undefined;
  }

  let iframe: HTMLIFrameElement;
  try {
    iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.display = 'none';
    host.appendChild(iframe);
    const realmPromise = iframe.contentWindow ? iframe.contentWindow['Promise'] : undefined;
    return realmPromise ? 'withResolvers' in realmPromise : undefined;
  } catch (e) {
    return undefined;
  } finally {
    if (iframe && iframe.parentElement) {
      iframe.parentElement.removeChild(iframe);
    }
  }
}
